<template>
  <v-card class="mb-4">
    <v-card-title class="primary white--text">
      {{ customerResult.customer_name }}
      <v-spacer></v-spacer>
      <v-chip small outlined color="white">
        {{ customerResult.customer_timezone }}
      </v-chip>
    </v-card-title>
    <v-card-text>
      <!-- Summary Metrics -->
      <v-row class="mt-2">
        <v-col cols="12">
          <h3 class="text-h6 mb-3">{{ $t('reports_ui.forecast_violation_validation_report.accuracy_metrics') }}</h3>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h4 primary--text">{{ formatPercent(metrics.accuracy) }}</div>
              <div class="text-subtitle-2">{{ $t('accuracy') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h4 success--text">{{ formatPercent(metrics.precision) }}</div>
              <div class="text-subtitle-2">{{ $t('precision') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h4 info--text">{{ formatPercent(metrics.recall) }}</div>
              <div class="text-subtitle-2">{{ $t('recall') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h4 secondary-900--text">{{ formatNumber(metrics.f1_score) }}</div>
              <div class="text-subtitle-2">{{ $t('f1_score') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Additional Metrics -->
      <v-row>
        <v-col cols="6" md="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5">{{ formatPercent(metrics.false_positive_rate) }}</div>
              <div class="text-caption">{{ $t('false_positive_rate') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="6" md="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5">{{ formatLeadTime(metrics.average_lead_time_minutes) }}</div>
              <div class="text-caption">{{ $t('avg_lead_time') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5">{{ metrics.total_validations }}</div>
              <div class="text-caption">{{ $t('total_validations') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Validation Counts -->
      <v-row class="mt-2">
        <v-col cols="12">
          <h3 class="text-h6 mb-3">{{ $t('reports_ui.forecast_violation_validation_report.validation_breakdown') }}</h3>
        </v-col>
        <v-col cols="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 success--text">{{ metrics.true_positives }}</div>
              <div class="text-caption">{{ $t('true_positives') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 warning--text">{{ metrics.false_positives }}</div>
              <div class="text-caption">{{ $t('false_positives') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card outlined>
            <v-card-text class="text-center">
              <div class="text-h5 error--text">{{ metrics.false_negatives }}</div>
              <div class="text-caption">{{ $t('false_negatives') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Breakdown Tables -->
      <v-row class="mt-4">
        <v-col cols="12" md="6">
          <h4 class="text-subtitle-1 mb-2">{{ $t('by_violation_type') }}</h4>
          <v-simple-table dense v-if="breakdown.by_violation_type.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $tc('violation_type') }}</th>
                  <th>{{ $t('result') }}</th>
                  <th class="text-right">{{ $t('count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in breakdown.by_violation_type" :key="index">
                  <td>{{ row.violation_type }}</td>
                  <td>{{ row.validation_result }}</td>
                  <td class="text-right">{{ row.count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-alert v-else type="info" dense text>{{ $t('no_data') }}</v-alert>
        </v-col>

        <v-col cols="12" md="6">
          <h4 class="text-subtitle-1 mb-2">{{ $t('by_zone') }}</h4>
          <v-simple-table dense v-if="breakdown.by_zone.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $tc('zone') }}</th>
                  <th>{{ $t('result') }}</th>
                  <th class="text-right">{{ $t('count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in breakdown.by_zone" :key="index">
                  <td>{{ row.zone }}</td>
                  <td>{{ row.validation_result }}</td>
                  <td class="text-right">{{ row.count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-alert v-else type="info" dense text>{{ $t('no_data') }}</v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <h4 class="text-subtitle-1 mb-2">{{ $t('by_forecast_phase') }}</h4>
          <v-simple-table dense v-if="breakdown.by_phase.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $t('phase') }}</th>
                  <th>{{ $t('result') }}</th>
                  <th class="text-right">{{ $t('count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in breakdown.by_phase" :key="index">
                  <td>{{ row.forecast_phase }}</td>
                  <td>{{ row.validation_result }}</td>
                  <td class="text-right">{{ row.count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-alert v-else type="info" dense text>{{ $t('no_data') }}</v-alert>
        </v-col>

        <v-col cols="12" md="6">
          <h4 class="text-subtitle-1 mb-2">{{ $t('by_forecasting_status') }}</h4>
          <v-simple-table dense v-if="breakdown.by_forecasting_status.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>{{ $t('forecasting_enabled') }}</th>
                  <th>{{ $t('result') }}</th>
                  <th class="text-right">{{ $t('count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in breakdown.by_forecasting_status" :key="index">
                  <td>{{ row.forecasting_enabled ? $t('yes') : $t('no') }}</td>
                  <td>{{ row.validation_result }}</td>
                  <td class="text-right">{{ row.count }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-alert v-else type="info" dense text>{{ $t('no_data') }}</v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ForecastViolationValidationReportCard',
  props: {
    customerResult: {
      type: Object,
      required: true
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    }
  },
  computed: {
    metrics () {
      return this.customerResult.metrics || {}
    },
    breakdown () {
      return this.customerResult.breakdown || {
        by_validation_result: [],
        by_violation_type: [],
        by_zone: [],
        by_phase: [],
        by_forecasting_status: []
      }
    }
  },
  methods: {
    formatPercent (value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }
      return `${parseFloat(value).toFixed(1)}%`
    },
    formatNumber (value) {
      if (value === null || value === undefined) {
        return 'N/A'
      }
      return parseFloat(value).toFixed(2)
    },
    formatLeadTime (minutes) {
      if (minutes === null || minutes === undefined || minutes === '') {
        return 'N/A'
      }
      return `${Math.round(minutes)} min`
    }
  }
}
</script>

<style scoped>
</style>
